import React from 'react';
import styled from '@emotion/styled';
import { sendAsCompany, sendAsKey, SendAsChoice } from 'domain/conversations/sendAs';
import { SelfUserProfile } from 'types/user';
import CompanyUserAvatar from 'ui/elements/avatars/CompanyUserAvatar';
import CheckmarkIcon from 'ui/elements/icons/CheckmarkIcon';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import Tooltip from 'ui/elements/Tooltip';
import PopMenu from 'ui/modules/PopMenu';
import { halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';

const OPTION_AVATAR_SIZE = 28;

const Anchor = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${quarterSpacing};
  padding: 0;
  border: none;
  background-color: transparent;
  cursor: pointer;
`;

const Option = styled.span`
  display: flex;
  align-items: center;
  gap: ${halfSpacing};
`;

/* Reserves the tick's width on every row so the names stay in one column. */
const Tick = styled.span`
  display: flex;
  width: 1rem;
  margin-left: ${halfSpacing};
`;

interface Props {
  /** The identity in force, and what else it could be. */
  sendAs?: SendAsChoice;
  self?: SelfUserProfile;
  size: number;
}

/**
 * The avatar inside the reply field: who this message goes out as. When more than one identity is
 * available it becomes a menu, because switching identity switches which thread you are in — a
 * community's conversation with a company is a different conversation from your own.
 */
export default function SendAsPicker({ sendAs, self, size }: Props) {
  const selected = sendAs?.selected;
  const options = sendAs?.options ?? [];
  const avatar = <CompanyUserAvatar company={sendAsCompany(selected)} user={self} size={size} />;

  if (options.length < 2 || !sendAs?.onChange) {
    return avatar;
  }

  return (
    <PopMenu
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      renderAnchor={onClick => (
        <Tooltip title="Change who you are writing as">
          <Anchor type="button" aria-label="Change who you are writing as" onClick={onClick}>
            {avatar}
            <ChevronDownIcon fontSize="tiny" color="dark-grey" />
          </Anchor>
        </Tooltip>
      )}
      items={options.map(option => {
        const isSelected = sendAsKey(option) === sendAsKey(sendAs.selected);
        return {
          selected: isSelected,
          spacing: 'compact' as const,
          onClick: () => sendAs.onChange?.(option),
          text: (
            <Option>
              <CompanyUserAvatar company={sendAsCompany(option)} user={self} size={OPTION_AVATAR_SIZE} />
              {sendAsCompany(option)?.name ?? self?.name ?? 'You'}
              <Tick>{isSelected && <CheckmarkIcon fontSize="tiny" color="indigo" />}</Tick>
            </Option>
          ),
        };
      })}
    />
  );
}
